import { formatQuotationMoney } from "./quotationHistory";
import { formatIDR } from "./format";

export const EMPTY_SERVICE_COST = Object.freeze({
  spare_part_costs: [],
  custom_costs: [],
  discount: {
    enabled: false,
    percentage: "0",
    effective_discount_percentage: "0",
    reason: "",
    manual_override: false,
  },
  summary: {
    gross_service_cost: "0",
    discount_amount: "0",
    final_service_cost: "0",
  },
});

export function isCanonicalDecimalZero(value) {
  return /^0+(?:\.0+)?$/.test(String(value ?? "").trim());
}

function normalizedCanonicalDecimal(value) {
  const match = String(value ?? "").trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  const integer = match[2].replace(/^0+(?=\d)/, "");
  const fraction = (match[3] || "").replace(/0+$/, "");
  const magnitude = fraction ? `${integer}.${fraction}` : integer;
  return magnitude === "0" ? "0" : `${match[1] === "-" ? "-" : ""}${magnitude}`;
}

export function areCanonicalDecimalsEqual(left, right) {
  const normalizedLeft = normalizedCanonicalDecimal(left);
  const normalizedRight = normalizedCanonicalDecimal(right);
  if (normalizedLeft === null || normalizedRight === null) {
    return String(left ?? "").trim() === String(right ?? "").trim();
  }
  return normalizedLeft === normalizedRight;
}

/** Mirrors the approved backend default for a discount that remains system-managed. */
export function systemManagedDiscount(category, dvtWarrantyStatus) {
  const customerPaid = category === "sold" && ["out_of_warranty", "void"].includes(dvtWarrantyStatus);
  return {
    enabled: !customerPaid,
    percentage: customerPaid ? "0" : "100",
    reason: customerPaid ? "" : "Ditanggung DVT",
    manual_override: false,
  };
}

export function serviceCostDiscountsEqual(left, right) {
  const normalizedLeft = normalizeServiceCostDiscountForComparison(left);
  const normalizedRight = normalizeServiceCostDiscountForComparison(right);
  return normalizedLeft.enabled === normalizedRight.enabled &&
    normalizedLeft.percentage === normalizedRight.percentage &&
    normalizedLeft.reason === normalizedRight.reason &&
    (left?.manual_override === true) === (right?.manual_override === true);
}

export function normalizeServiceCostDiscountForComparison(discount) {
  return {
    enabled: discount?.enabled === true,
    percentage: normalizedCanonicalDecimal(discount?.percentage ?? "0") ?? String(discount?.percentage ?? "0").trim(),
    reason: String(discount?.reason ?? "").trim(),
  };
}

/**
 * Returns a review-only system discount when the backend will change it after
 * Category/DVT final-state processing. A null result means keep the current
 * draft discount and do not create a Review discount mutation.
 */
export function projectSystemManagedDiscount({
  originalDiscount,
  originalCategory,
  finalCategory,
  originalDvtWarrantyStatus,
  finalDvtWarrantyStatus,
  hasDirectDiscountMutation = false,
}) {
  const defaultInputsChanged = originalCategory !== finalCategory ||
    originalDvtWarrantyStatus !== finalDvtWarrantyStatus;
  if (!defaultInputsChanged || originalDiscount?.manual_override === true || hasDirectDiscountMutation) {
    return null;
  }
  const projected = systemManagedDiscount(finalCategory, finalDvtWarrantyStatus);
  return serviceCostDiscountsEqual(originalDiscount, projected) ? null : projected;
}

export function serviceCostSummary(value) {
  const cost = value?.summary || value || {};
  return {
    gross: String(cost.gross_service_cost ?? "0"),
    discountAmount: String(cost.discount_amount ?? "0"),
    final: String(cost.final_service_cost ?? "0"),
    effectiveDiscount: String(
      cost.effective_discount_percentage ??
        value?.discount?.effective_discount_percentage ??
        value?.effective_discount_percentage ??
        "0"
    ),
  };
}

export function serviceCostDraft(serviceCost) {
  const value = serviceCost || EMPTY_SERVICE_COST;
  return {
    custom_costs: (value.custom_costs || []).map((row) => ({
      ...row,
      quantity: String(row.quantity ?? 1),
      unit_price: String(row.unit_price ?? "0"),
      _key: row.id || `new-${Math.random().toString(36).slice(2)}`,
    })),
    discount: {
      enabled: value.discount?.enabled === true,
      percentage: String(value.discount?.percentage ?? "0"),
      reason: String(value.discount?.reason ?? ""),
    },
  };
}

const NON_NEGATIVE_DECIMAL = /^\d+(?:\.(\d+))?$/;
const POSITIVE_INTEGER = /^[1-9]\d*$/;
export const MAX_SERVICE_COST_QUANTITY = Number.MAX_SAFE_INTEGER;

export function parseServiceCostQuantity(value) {
  const text = String(value ?? "");
  if (!POSITIVE_INTEGER.test(text)) return null;
  const quantity = Number(text);
  return Number.isSafeInteger(quantity) ? quantity : null;
}

function parseServiceCostDecimal(value) {
  const text = String(value ?? "").trim();
  const match = NON_NEGATIVE_DECIMAL.exec(text);
  if (!match) return null;
  const fraction = match[1] || "";
  return {
    units: BigInt(`${text.split(".")[0]}${fraction}`),
    scale: fraction.length,
  };
}

const decimalPower = (scale) => 10n ** BigInt(scale);

function decimalToCanonical({ units, scale }) {
  if (scale === 0) return units.toString();
  const padded = units.toString().padStart(scale + 1, "0");
  const whole = padded.slice(0, -scale);
  const fraction = padded.slice(-scale).replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
}

function addServiceCostDecimals(left, right) {
  const scale = Math.max(left.scale, right.scale);
  return {
    units:
      left.units * decimalPower(scale - left.scale) +
      right.units * decimalPower(scale - right.scale),
    scale,
  };
}

function roundHalfUp(numerator, denominator) {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  return quotient + (remainder * 2n >= denominator ? 1n : 0n);
}

/** Derive the transient Edit/Review preview from the actual request draft. */
export function deriveServiceCostDraftPreview(serviceCost, draft) {
  let gross = { units: 0n, scale: 0 };

  for (const row of serviceCost?.spare_part_costs || []) {
    const lineTotal = parseServiceCostDecimal(row.line_total);
    if (lineTotal) gross = addServiceCostDecimals(gross, lineTotal);
  }

  const customLineTotals = {};
  for (const row of draft?.custom_costs || []) {
    const unitPrice = parseServiceCostDecimal(row.unit_price);
    const quantityText = String(row.quantity ?? "");
    const key = row._key || row.id;
    if (!unitPrice || !POSITIVE_INTEGER.test(quantityText)) {
      customLineTotals[key] = null;
      continue;
    }
    const lineTotal = { units: unitPrice.units * BigInt(quantityText), scale: unitPrice.scale };
    customLineTotals[key] = decimalToCanonical(lineTotal);
    gross = addServiceCostDecimals(gross, lineTotal);
  }

  const percentage = parseServiceCostDecimal(draft?.discount?.percentage);
  let discountAmount = 0n;
  if (draft?.discount?.enabled === true && percentage) {
    const numerator = gross.units * percentage.units;
    const denominator = 100n * decimalPower(gross.scale + percentage.scale);
    const rounded = roundHalfUp(numerator, denominator);
    const maximumWholeRupiah = gross.units / decimalPower(gross.scale);
    discountAmount = rounded < maximumWholeRupiah ? rounded : maximumWholeRupiah;
  }

  const final = {
    units: gross.units - discountAmount * decimalPower(gross.scale),
    scale: gross.scale,
  };
  return {
    customLineTotals,
    discount: {
      enabled: draft?.discount?.enabled === true,
      percentage: String(draft?.discount?.percentage ?? "0"),
      effective_discount_percentage:
        draft?.discount?.enabled === true && percentage ? decimalToCanonical(percentage) : "0",
      reason: String(draft?.discount?.reason ?? ""),
    },
    summary: {
      gross_service_cost: decimalToCanonical(gross),
      discount_amount: discountAmount.toString(),
      final_service_cost: decimalToCanonical(final),
    },
  };
}

export function normalizeCustomCostForComparison(row) {
  const quantity = String(row?.quantity ?? "").trim();
  const parsedQuantity = parseServiceCostQuantity(quantity);
  return {
    description: String(row?.description || "").trim(),
    quantity: parsedQuantity === null ? quantity : String(parsedQuantity),
    unit_price: normalizedCanonicalDecimal(row?.unit_price) ?? String(row?.unit_price ?? "").trim(),
  };
}

export function buildServiceCostMutation(originalCost, draft, expectedUpdatedAt) {
  const originalRows = originalCost?.custom_costs || [];
  const originalById = new Map(originalRows.map((row) => [row.id, row]));
  const currentIds = new Set(draft.custom_costs.filter((row) => row.id).map((row) => row.id));
  const commands = [];

  for (const row of draft.custom_costs) {
    const current = normalizeCustomCostForComparison(row);
    const quantity = parseServiceCostQuantity(current.quantity);
    if (!row.id) {
      commands.push({
        action: "add",
        description: current.description,
        quantity: quantity ?? current.quantity,
        unit_price: current.unit_price,
      });
      continue;
    }
    const original = originalById.get(row.id);
    if (JSON.stringify(current) !== JSON.stringify(normalizeCustomCostForComparison(original))) {
      commands.push({
        action: "edit",
        id: row.id,
        expected_updated_at: original.updated_at,
        description: current.description,
        quantity: quantity ?? current.quantity,
        unit_price: current.unit_price,
      });
    }
  }
  for (const row of originalRows) {
    if (!currentIds.has(row.id)) {
      commands.push({ action: "remove", id: row.id, expected_updated_at: row.updated_at });
    }
  }

  const originalDiscount = normalizeServiceCostDiscountForComparison(originalCost?.discount);
  const nextDiscount = {
    enabled: draft.discount.enabled === true,
    percentage: String(draft.discount.percentage || "0").trim(),
    reason: String(draft.discount.reason || "").trim(),
  };
  const mutation = {};
  if (commands.length) mutation.custom_cost_commands = commands;
  // `manual_override` is backend-owned metadata. It must not turn an unchanged
  // editable discount into a mutation when opening and submitting a Move.
  if (JSON.stringify(originalDiscount) !== JSON.stringify(normalizeServiceCostDiscountForComparison(nextDiscount))) {
    mutation.discount = nextDiscount;
  }
  if (Object.keys(mutation).length) mutation.expected_updated_at = expectedUpdatedAt;
  return mutation;
}

export function validateServiceCostDraft(draft) {
  for (const row of draft.custom_costs) {
    if (!String(row.description || "").trim()) return "Custom Cost description is required.";
    const quantity = String(row.quantity || "");
    if (!POSITIVE_INTEGER.test(quantity)) return "Custom Cost quantity must be a positive integer.";
    if (parseServiceCostQuantity(quantity) === null) {
      return `Custom Cost quantity must not exceed ${MAX_SERVICE_COST_QUANTITY}.`;
    }
    if (!/^\d+(?:\.\d+)?$/.test(String(row.unit_price || "").trim())) return "Custom Cost Unit Price must be a non-negative amount.";
  }
  const percentage = String(draft.discount.percentage || "0").trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(percentage) || Number(percentage) > 100) {
    return "Discount must be between 0 and 100 with at most two decimals.";
  }
  return "";
}

export function formatServiceCost(value) {
  return formatQuotationMoney(value ?? "0");
}

export function formatServiceCostWholeRupiah(value) {
  const text = String(value ?? "0").trim();
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(text)) return text;
  return formatIDR(text);
}
